import { create } from 'zustand'
import { trpcClient } from '@/lib/trpc-client'
import type { Deal, DealInput } from '@/lib/schema'

interface DealState {
  deals: Deal[]
  currentDeal: Deal | null
  isLoading: boolean
  error: string | null
  lastFetched: string | null
}

interface DealActions {
  fetchDeals: () => Promise<void>
  getDeal: (id: string) => Promise<Deal | null>
  createDeal: (data: DealInput) => Promise<Deal>
  updateDeal: (id: string, updates: Partial<DealInput>) => Promise<Deal>
  deleteDeal: (id: string) => Promise<void>
  setCurrentDeal: (deal: Deal | null) => void
  reset: () => void
}

const initialState: DealState = {
  deals: [],
  currentDeal: null,
  isLoading: false,
  error: null,
  lastFetched: null,
}

export const useDealStore = create<DealState & DealActions>((set, get) => ({
  ...initialState,

  fetchDeals: async () => {
    set({ isLoading: true, error: null })
    try {
      const { deals } = await trpcClient.deal.list.query()
      set({ deals, isLoading: false, lastFetched: new Date().toISOString() })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  getDeal: async (id: string) => {
    // Check if deal is already in cache
    const cached = get().deals.find((d) => d.id === id)
    if (cached) {
      set({ currentDeal: cached })
      return cached
    }

    set({ isLoading: true, error: null })
    try {
      const { deal } = await trpcClient.deal.get.query({ id })
      set({ currentDeal: deal, isLoading: false })
      return deal
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  createDeal: async (data: DealInput) => {
    set({ isLoading: true, error: null })
    try {
      const { deal } = await trpcClient.deal.create.mutate(data)
      set((state) => ({
        deals: [deal, ...state.deals],
        isLoading: false,
      }))
      return deal
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  updateDeal: async (id: string, updates: Partial<DealInput>) => {
    set({ isLoading: true, error: null })
    try {
      const { deal } = await trpcClient.deal.update.mutate({ id, deal: updates })
      set((state) => ({
        deals: state.deals.map((d) => (d.id === id ? deal : d)),
        currentDeal: state.currentDeal?.id === id ? deal : state.currentDeal,
        isLoading: false,
      }))
      return deal
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  deleteDeal: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await trpcClient.deal.delete.mutate({ id })
      set((state) => ({
        deals: state.deals.filter((d) => d.id !== id),
        currentDeal: state.currentDeal?.id === id ? null : state.currentDeal,
        isLoading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  setCurrentDeal: (deal: Deal | null) => {
    set({ currentDeal: deal })
  },

  reset: () => {
    set(initialState)
  },
}))
